import React from "react";
import { Seeker } from "../../types/seeker";

interface SeekerSurveyResultsProps {
    seeker: Seeker;
}

interface SurveyFieldProps {
    label: string;
    value: string | null | undefined;
    fallback?: string;
}

function SurveyField({ label: fieldLabel, value: fieldValue, fallback = "Not specified" }: SurveyFieldProps) {
    return (
        <div className="space-y-1 rounded-xl border border-[#2C2731]/15 bg-[#FFFCEF] px-4 py-3">
            <div className="text-xs font-semibold uppercase tracking-[0.1em] text-[#403851]">{fieldLabel}</div>
            <div className="text-sm text-[#2C2731]">{fieldValue || fallback}</div>
        </div>
    );
}

export default function SeekerSurveyResults({ seeker }: SeekerSurveyResultsProps) {
    const formatFocusPreferences = () => {
        if (!seeker.focusPreferences) return "Not specified";
        
        return Object.entries(seeker.focusPreferences)
            .filter(([_, values]) => values && Array.isArray(values) && values.length > 0)
            .map(([category, values]) => {
                const categoryName = category.replace('focus_', '').replace('_pref', '').replace('_', ' ');
                return `${categoryName}: ${(values as string[]).join(', ')}`;
            })
            .join(' | ') || "Not specified";
    };

    const formatArrayField = (field: string[] | undefined, fallback: string = "Not specified") => {
        return field && field.length > 0 ? field.join(", ") : fallback;
    };

    const formatBooleanField = (field: string | undefined, yesText: string = "Yes", noText: string = "No") => {
        if (field === undefined) return "Not specified";
        return field === 'Yes' ? yesText : noText;
    };

    const surveyData = [
        {
            label: "Primary Goals",
            value: formatFocusPreferences()
        },
        {
            label: "Experience Level",
            value: formatArrayField(seeker.experienceTypes, "No prior experience specified")
        },
        {
            label: "Preferred Setting",
            value: formatArrayField(seeker.supportTypes, "Not specified")
        },
        {
            label: "Medical Conditions",
            value: formatArrayField(seeker.clinicalDiagnoses, "None reported")
        },
        {
            label: "Location",
            value: seeker.locationPostcode || "Not specified"
        },
        {
            label: "Gender",
            value: seeker.seekerGenders && seeker.seekerGenders.length > 0
                ? formatArrayField(seeker.seekerGenders, "Not specified")
                : seeker.seekerGender || "Not specified"
        },
        {
            label: "Medicine Comfort Level",
            value: formatArrayField(seeker.medicineOptionsComfort, "Not specified")
        },
        {
            label: "Travel Willingness",
            value: formatArrayField(seeker.travelWillingness, "Not specified")
        },
        {
            label: "Travel Distance",
            value: seeker.travelDistance || "Not specified"
        },
        {
            label: "Specialist Groups Preference",
            value: formatArrayField(seeker.preferSpecialistInGroups, "Not specified")
        },
        {
            label: "Religion Alignment Important",
            value: formatBooleanField(seeker.religionAlignmentImportant, "Yes", "No")
        },
        {
            label: "Beliefs",
            value: seeker.seekerBeliefs || "Not specified"
        },
        {
            label: "Currently on Medication",
            value: formatBooleanField(seeker.currentlyOnMeds, "Yes", "No")
        },
        {
            label: "Budget Band",
            value: seeker.budgetBand || "Not specified"
        },
        {
            label: "Additional Information",
            value: seeker.additionalInfo || "None provided"
        },
        {
            label: "Referral Source",
            value: seeker.referralSource || "Not specified"
        }
    ];

    return (
        <div className="rounded-2xl border border-[#2C2731] bg-[#F2EAC3] shadow-[inset_0_0_0_1px_rgba(44,39,49,0.05)]">
            <div className="border-b border-[#2C2731]/20 px-6 pb-4 pt-6">
                <h3 className="text-lg font-semibold text-[#2C2731]">Survey Results</h3>
                <p className="text-xs font-medium uppercase tracking-[0.1em] text-[#403851]">
                    Key preferences and intake answers
                </p>
            </div>

            <div className="grid max-h-[18.5rem] gap-4 overflow-auto px-6 py-6">
                {surveyData.map((item, index) => (
                    <SurveyField key={index} label={item.label} value={item.value} />
                ))}
            </div>
        </div>
    );
}
